import React, { useState, useEffect } from 'react';
import { ChevronDown, Plus, PencilIcon, Save, X, Trash2 } from 'lucide-react';
import { collection, onSnapshot, query, where, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Link } from 'react-router-dom';
import { auth } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';

const PrewrittenNotes = ({ onNoteSelect, className = "" }) => {
  const [user, loading] = useAuthState(auth);
  const [categories, setCategories] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Load categories and notes from Firebase
  useEffect(() => {
    if (loading) return; // Wait for auth to initialize
    if (!user) {
      setIsLoading(false);
      return;
    }

    let unsubCategories;
    let unsubNotes;

    const loadData = async () => {
      setIsLoading(true);
      
      try {
        const categoriesRef = collection(db, 'noteCategories');
        const notesRef = collection(db, 'prewrittenNotes');
        
        // Load both categories and notes concurrently
        unsubCategories = onSnapshot(categoriesRef, (snapshot) => {
          const data = [];
          snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
          setCategories(data);
        });

        unsubNotes = onSnapshot(notesRef, (snapshot) => {
          const noteData = [];
          snapshot.forEach((doc) => noteData.push({ id: doc.id, ...doc.data() }));
          setNotes(noteData);
        });
      } catch (error) {
        console.error('Error loading notes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubCategories) unsubCategories();
      if (unsubNotes) unsubNotes();
    };
  }, [user, loading]);

  // When category is selected, log its notes
  useEffect(() => {
    if (selectedCategory) {
      const categoryNotes = notes.filter(note => note.category === selectedCategory);
      console.log('Notes for selected category:', categoryNotes);
    }
  }, [selectedCategory, notes]);

  const availableCategories = categories.filter(category => 
    notes.some(note => note.category === category.id)
  );

  if (loading || !user) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">
          Add Prewritten Note
        </label>
        <div className="flex gap-2">
          <Link 
            to="/admin/notes"
            className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center"
          >
            <PencilIcon className="w-4 h-4 mr-1" />
            Edit Notes
          </Link>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center"
          >
            {isOpen ? 'Close Notes' : 'Open Notes'}
          </button>
        </div>
      </div>

      {isOpen && !isLoading && (
        <div>
          <select
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white/50 mb-4"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Select a category...</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.title || category.name}
              </option>
            ))}
          </select>

          {selectedCategory && (
            <div className="space-y-3">
              {notes
                .filter(note => note.category === selectedCategory)
                .map((note) => (
                  <div
                    key={note.id}
                    className="flex items-start gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => onNoteSelect(note.text)}
                      className="flex-shrink-0 text-cyan-600 hover:text-cyan-700"
                    >
                      + Add
                    </button>
                    <span className="text-slate-600">{note.text}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {isOpen && isLoading && (
        <div className="text-center py-4 text-slate-500">
          Loading notes...
        </div>
      )}
    </div>
  );
};

export default PrewrittenNotes; 